import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createEmptyInstitution,
  isArchivedInstitution,
  mapInstitutionToRow,
} from '@/modules/financials/utils';

const useFinancialInstitutionRows = ({ open, sourceInstitutions }) => {
  const institutionIdRef = useRef(1);
  const [institutions, setInstitutions] = useState([createEmptyInstitution(0)]);
  const [archivedInstitutions, setArchivedInstitutions] = useState([]);
  const [deletedInstitutionIds, setDeletedInstitutionIds] = useState([]);

  const resetInstitutions = useCallback(() => {
    if (!sourceInstitutions.length) {
      setInstitutions([createEmptyInstitution(institutionIdRef.current)]);
      institutionIdRef.current += 1;
      setArchivedInstitutions([]);
      setDeletedInstitutionIds([]);
      return;
    }

    const activeInstitutions = sourceInstitutions.filter(
      (institution) => !isArchivedInstitution(institution)
    );
    const nextArchivedInstitutions = sourceInstitutions
      .filter(isArchivedInstitution)
      .map((institution) => {
        const row = mapInstitutionToRow(institution, institutionIdRef.current);
        institutionIdRef.current += 1;
        return row;
      });

    setInstitutions(
      activeInstitutions.map((institution) => {
        const row = mapInstitutionToRow(institution, institutionIdRef.current);
        institutionIdRef.current += 1;
        return row;
      })
    );
    setArchivedInstitutions(nextArchivedInstitutions);
    setDeletedInstitutionIds([]);
  }, [sourceInstitutions]);

  useEffect(() => {
    if (open) {
      resetInstitutions();
    }
  }, [open, resetInstitutions]);

  const addInstitution = useCallback(() => {
    setInstitutions((currentInstitutions) => [
      ...currentInstitutions,
      createEmptyInstitution(institutionIdRef.current),
    ]);
    institutionIdRef.current += 1;
  }, []);

  const removeInstitution = useCallback((institutionId) => {
    setInstitutions((currentInstitutions) => {
      const institutionToRemove = currentInstitutions.find(
        (institution) => institution.clientId === institutionId
      );

      if (
        institutionToRemove?.answerId &&
        !institutionToRemove.pendingArchive
      ) {
        setArchivedInstitutions((currentArchivedInstitutions) => [
          ...currentArchivedInstitutions,
          {
            ...institutionToRemove,
            error: '',
            pendingArchive: !institutionToRemove.restoredFromArchive,
            restoredFromArchive: false,
          },
        ]);
      }

      if (
        institutionToRemove?.answerId &&
        !institutionToRemove.restoredFromArchive
      ) {
        setDeletedInstitutionIds((currentDeletedIds) => [
          ...currentDeletedIds,
          institutionToRemove.answerId,
        ]);
      }

      if (currentInstitutions.length === 1 && !institutionToRemove?.answerId) {
        return [createEmptyInstitution(currentInstitutions[0].clientId)];
      }

      return currentInstitutions.filter(
        (institution) => institution.clientId !== institutionId
      );
    });
  }, []);

  const updateInstitutionName = useCallback((institutionId, name) => {
    setInstitutions((currentInstitutions) =>
      currentInstitutions.map((institution) =>
        institution.clientId === institutionId
          ? { ...institution, name, error: '' }
          : institution
      )
    );
  }, []);

  const restoreInstitution = useCallback((institutionId) => {
    setArchivedInstitutions((currentArchivedInstitutions) => {
      const institutionToRestore = currentArchivedInstitutions.find(
        (institution) => institution.clientId === institutionId
      );

      if (!institutionToRestore) {
        return currentArchivedInstitutions;
      }

      setInstitutions((currentInstitutions) => [
        ...currentInstitutions,
        {
          ...institutionToRestore,
          error: '',
          pendingArchive: false,
          restoredFromArchive: !institutionToRestore.pendingArchive,
        },
      ]);

      if (institutionToRestore.pendingArchive) {
        setDeletedInstitutionIds((currentDeletedIds) =>
          currentDeletedIds.filter(
            (answerId) => answerId !== institutionToRestore.answerId
          )
        );
      }

      return currentArchivedInstitutions.filter(
        (institution) => institution.clientId !== institutionId
      );
    });
  }, []);

  return {
    institutions,
    archivedInstitutions,
    deletedInstitutionIds,
    setInstitutions,
    resetInstitutions,
    addInstitution,
    removeInstitution,
    updateInstitutionName,
    restoreInstitution,
  };
};

export default useFinancialInstitutionRows;
